/**
 * GoogleSignIn - Custom styled Google sign-in button
 *
 * Auth Code Flow with Popup:
 * 1. User clicks our custom AppButton
 * 2. requestGoogleAuthCode opens Google popup (GIS SDK code model)
 * 3. Google returns authorization code (safe to transmit)
 * 4. We POST code to BFF endpoint /api/auth/google
 * 5. Hono BFF exchanges code for tokens with Google (using client_secret)
 * 6. Hono BFF sends id_token to Rust backend for validation
 * 7. Rust backend validates, creates session, returns token
 * 8. Hono BFF sets HTTP-only cookie
 *
 * Requires: Cross-Origin-Opener-Policy: same-origin-allow-popups header
 *
 * W3.2 (auth-debt epic): errors ride the SAME typed reason→catalog
 * pipeline as the OTP flow (`useLocalizeApiError`) and render inline —
 * the former `onError?: (error: string)` prop carried hardcoded English
 * and had NO consumer, so every Google failure was silently dropped.
 */
import { useRouter } from '@/shared/i18n/routing';
import { useLocalizeApiError } from '@/shared/i18n/useLocalizeApiError';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { bff } from '@/shared/lib/bff';
import { clientLogger } from '@/shared/lib/client-logger';
import { requestGoogleAuthCode } from '@letsgethome/lib/auth/google-oauth';
import type { UserProfile } from '@letsgethome/lib/auth/types';
import { bffError } from '@letsgethome/lib/server-errors';
import type { GoogleClientId, LocalizedText } from '@letsgethome/types/branded';
import { AppButton, inFlight } from '@letsgethome/ui/app-button';
import { GoogleIcon } from '@letsgethome/ui/icons/google-icon';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

interface GoogleSignInProps {
  clientId: GoogleClientId;
  onSuccess?: (user?: UserProfile) => void;
}

export function GoogleSignIn({ clientId, onSuccess }: GoogleSignInProps) {
  const router = useRouter();
  const tShared = useTranslations('auth.shared');
  const tGoogle = useTranslations('auth.google');
  const localizeApiError = useLocalizeApiError();
  const [error, setError] = useState<LocalizedText | null>(null);

  const signInMutation = useMutation({
    mutationFn: async (code: string) => {
      const result = await bff('/api/auth/google', { code });
      if (!result.success) throw bffError(result.error, 'Google sign-in failed');
      return result.data;
    },
    onSuccess: (user) => {
      setError(null);
      void router.refresh();
      onSuccess?.(user);
    },
    onError: (err) => setError(localizeApiError(err, 'auth.google.errorSignIn')),
  });

  const handleClick = useCallback(() => {
    setError(null);
    requestGoogleAuthCode({ clientId })
      .then((code) => signInMutation.mutate(code))
      .catch((err: unknown) => {
        clientLogger.error(
          { error: err instanceof Error ? err : new Error(String(err)), component: 'GoogleSignIn' },
          'OAuth popup failed'
        );
        setError(tGoogle('errorCancelled'));
      });
  }, [clientId, signInMutation, tGoogle]);

  if (!clientId) {
    clientLogger.warn(
      { component: 'GoogleSignIn' },
      'clientId not provided — Google Sign-In disabled'
    );
    return null;
  }

  return (
    <div className="space-y-2">
      <AppButton
        variant="secondary"
        fullWidth
        loading={inFlight(signInMutation.isPending)}
        onClick={handleClick}
        type="button"
      >
        <GoogleIcon className="mr-2" />
        {tShared('continueWithProvider', { provider: 'Google' })}
      </AppButton>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
